import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  listLeads,
  getLead,
  patchLead,
  deleteLead,
  LEAD_KINDS,
  LEAD_STATUSES,
  LEAD_KIND_LABEL,
  type LeadCounts,
  type LeadKind,
  type LeadStatus,
  importLeads,
  type RemoteLead
} from '../lib/adminClient'
import { parseLeadEmails } from '../lib/leadEmailParse'
import { dateTime } from '../lib/format'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead, NoMatches } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { AccountLink, DrillValue } from '../components/Drill'

// Leads — every form sent on the website, in one list that can be filtered,
// searched and worked through.
//
// What it is not: a second source of truth. The site emails the team AND posts
// the lead to the server; this reads the server's copy. A lead that arrived
// before the ingest was wired, or while it was down, exists only as an email,
// and the empty state says so rather than implying the list is complete.
//
// Nothing here is invented. A lead that carried no campaign shows "unknown",
// not a guess, and the counts come from the server.

const STATUS_STYLE: Record<LeadStatus, string> = {
  new: 'bg-sky-500/15 text-sky-300',
  contacted: 'bg-amber-500/15 text-amber-300',
  qualified: 'bg-violet-500/15 text-violet-300',
  won: 'bg-emerald-500/15 text-emerald-300',
  lost: 'bg-white/5 text-slate-400'
}

const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  won: 'Won',
  lost: 'Lost'
}

function ago(iso: string, now: number): string {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const s = Math.max(0, Math.round((now - t) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

function StatusPill({ status }: { status: LeadStatus }): JSX.Element {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] ${STATUS_STYLE[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  )
}

// Where the lead came from, in the one line an operator needs. Shows the
// campaign when the page carried one, the referring page otherwise, and says
// plainly when neither is known.
function SourceCell({ lead }: { lead: RemoteLead }): JSX.Element {
  const campaign = lead.utmCampaign || lead.utmSource
  if (!campaign && !lead.landingPath) {
    return <span className="text-slate-600 text-xs">unknown</span>
  }
  return (
    <div className="text-[11px] leading-relaxed">
      {campaign ? (
        <div className="text-slate-200">
          {lead.utmSource ?? '—'}
          {lead.utmCampaign ? ` · ${lead.utmCampaign}` : ''}
          {lead.utmMedium ? <span className="text-slate-500"> ({lead.utmMedium})</span> : null}
        </div>
      ) : (
        <div className="text-slate-500">no campaign</div>
      )}
      {lead.landingPath && <div className="text-slate-500 truncate">{lead.landingPath}</div>}
      {lead.clickId && <div className="text-slate-600">{lead.clickId.split('=')[0]}</div>}
    </div>
  )
}

// Work address or mailbox provider. `freeMail` only says the domain is one we
// recognise as a provider, so the other case is phrased as a question rather
// than claimed as a company.
function AddressCell({ lead }: { lead: RemoteLead }): JSX.Element {
  return (
    <div>
      <div className="font-medium text-slate-100">{lead.email}</div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] ${
            lead.freeMail ? 'bg-white/5 text-slate-400' : 'bg-emerald-500/10 text-emerald-300'
          }`}
          title={
            lead.freeMail
              ? 'A consumer mailbox provider'
              : 'Not a mailbox provider we recognise — likely a work domain'
          }
        >
          {lead.freeMail ? 'personal' : lead.emailDomain || 'domain?'}
        </span>
        {lead.accountId && (
          <span
            className="px-1.5 py-0.5 rounded text-[10px] bg-violet-500/15 text-violet-300"
            title="An account already exists for this address"
          >
            has account
          </span>
        )}
        {lead.submissionCount > 1 && (
          <span
            className="px-1.5 py-0.5 rounded text-[10px] bg-sky-500/15 text-sky-300"
            title="This address has sent more than one form"
          >
            ×{lead.submissionCount}
          </span>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element | null {
  if (!value) return null
  return (
    <div className="flex gap-3 text-xs py-1 border-b border-white/5 last:border-0">
      <div className="w-28 shrink-0 text-slate-500">{label}</div>
      <div className="text-slate-200 break-words min-w-0">{value}</div>
    </div>
  )
}

// One row per lead, with every field the forms collect. Quoted so a comma or a
// newline in a message cannot shift the columns.
function toCsv(rows: RemoteLead[]): string {
  const cols: Array<[string, (l: RemoteLead) => string]> = [
    ['Received', (l) => l.receivedAt],
    ['Kind', (l) => LEAD_KIND_LABEL[l.kind] ?? l.kind],
    ['Status', (l) => STATUS_LABEL[l.status] ?? l.status],
    ['Name', (l) => l.name],
    ['Email', (l) => l.email],
    ['Domain', (l) => l.emailDomain],
    ['Personal mailbox', (l) => (l.freeMail ? 'yes' : 'no')],
    ['Has account', (l) => (l.accountId ? 'yes' : 'no')],
    ['Submissions', (l) => String(l.submissionCount)],
    ['Company', (l) => l.company ?? ''],
    ['Role', (l) => l.role ?? ''],
    ['Team size', (l) => l.teamSize ?? ''],
    ['Message', (l) => l.message ?? ''],
    ['Source', (l) => l.utmSource ?? ''],
    ['Medium', (l) => l.utmMedium ?? ''],
    ['Campaign', (l) => l.utmCampaign ?? ''],
    ['Content', (l) => l.utmContent ?? ''],
    ['Click id', (l) => l.clickId ?? ''],
    ['Landing page', (l) => l.landingPath ?? ''],
    ['Page', (l) => l.page ?? ''],
    ['Owner', (l) => l.ownerAdminEmail ?? ''],
    ['Notes', (l) => l.notes ?? '']
  ]
  const q = (s: string): string => `"${s.replace(/"/g, '""')}"`
  return [
    cols.map(([h]) => q(h)).join(','),
    ...rows.map((l) => cols.map(([, f]) => q(f(l))).join(','))
  ].join('\r\n')
}

function Detail({
  lead,
  history,
  onPatched,
  onDeleted,
  onClose
}: {
  lead: RemoteLead
  history: RemoteLead[]
  onPatched: (l: RemoteLead) => void
  onDeleted: (id: string) => void
  onClose: () => void
}): JSX.Element {
  const [notes, setNotes] = useState(lead.notes ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    setNotes(lead.notes ?? '')
    setConfirmDelete(false)
    setError(null)
  }, [lead.id, lead.notes])

  async function setStatus(status: LeadStatus): Promise<void> {
    setSaving(true)
    setError(null)
    const res = await patchLead(lead.id, { status })
    setSaving(false)
    if (res.ok) onPatched(res.data.lead)
    else setError(`Could not change the status: ${res.error}`)
  }

  async function saveNotes(): Promise<void> {
    setSaving(true)
    setError(null)
    const res = await patchLead(lead.id, { notes })
    setSaving(false)
    if (res.ok) onPatched(res.data.lead)
    else setError(`Could not save the note: ${res.error}`)
  }

  async function remove(): Promise<void> {
    setSaving(true)
    setError(null)
    const res = await deleteLead(lead.id)
    setSaving(false)
    if (res.ok) onDeleted(lead.id)
    else {
      setConfirmDelete(false)
      setError(
        res.status === 403
          ? 'Deleting a lead needs an admin or owner account. Nothing was deleted.'
          : `Could not delete it: ${res.error}`
      )
    }
  }

  const noteDirty = notes !== (lead.notes ?? '')

  return (
    <div className="p-2" data-testid="lead-detail">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
            {LEAD_KIND_LABEL[lead.kind] ?? lead.kind}
          </div>
          <div className="text-lg font-semibold mt-0.5">{lead.name || lead.email}</div>
          <a
            href={`mailto:${lead.email}`}
            className="text-sm text-accent hover:underline"
            data-testid="lead-mailto"
          >
            {lead.email}
          </a>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">
          Close
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg px-3 py-2 text-sm bg-red-500/10 text-red-300" role="alert">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <span className="text-xs text-slate-500 mr-1">Status</span>
        {LEAD_STATUSES.map((s) => (
          <button
            key={s}
            disabled={saving || s === lead.status}
            onClick={() => void setStatus(s)}
            data-testid={`lead-status-${s}`}
            className={`px-2.5 py-1 rounded-md text-xs border disabled:opacity-100 ${
              s === lead.status
                ? 'border-accent/60 bg-accent/15 text-white'
                : 'border-white/10 text-slate-300 hover:border-white/25 disabled:opacity-40'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">
            What they sent
          </div>
          <Row label="Name" value={lead.name || null} />
          <Row label="Company" value={lead.company} />
          <Row label="Role" value={lead.role} />
          <Row label="Team size" value={lead.teamSize} />
          <Row label="Message" value={lead.message} />
          {!lead.name && !lead.company && !lead.role && !lead.teamSize && !lead.message && (
            <div className="text-xs text-slate-500 py-1">
              Only an address. This form asks for nothing else.
            </div>
          )}

          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mt-5 mb-1">
            Where it came from
          </div>
          <Row label="Source" value={lead.utmSource} />
          <Row label="Medium" value={lead.utmMedium} />
          <Row label="Campaign" value={lead.utmCampaign} />
          <Row label="Content" value={lead.utmContent} />
          <Row label="Term" value={lead.utmTerm} />
          <Row label="Click id" value={lead.clickId} />
          <Row label="Landing page" value={lead.landingPath} />
          <Row label="Full page" value={lead.page} />
          {!lead.utmSource && !lead.utmCampaign && !lead.clickId && (
            <div className="text-xs text-slate-500 py-1">
              No campaign on the page this was sent from, so the source is unknown. It is not
              guessed.
            </div>
          )}
          <Row label="Received" value={dateTime(lead.receivedAt)} />
          <Row label="Stored" value={dateTime(lead.createdAt)} />
          <Row label="Last changed" value={dateTime(lead.updatedAt)} />
          <Row label="Account" value={lead.accountId ? <AccountLink id={lead.accountId}>{lead.accountId}</AccountLink> : null} />

          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mt-5 mb-1">
            The record
          </div>
          <Row label="Email domain" value={`${lead.emailDomain}${lead.freeMail ? ' (free mail, not a company domain)' : ' (a company domain)'}`} />
          <Row
            label="Times sent"
            value={
              <DrillValue to={{ set: 'leads', filters: { email: lead.email } }} label={`Forms from ${lead.email}`} expect={{ key: 'count', value: lead.submissionCount }} testId="lead-submissions">
                {`${lead.submissionCount} ${lead.submissionCount === 1 ? 'submission' : 'submissions'} from this address`}
              </DrillValue>
            }
          />
          <Row label="Worked by" value={lead.ownerAdminEmail ?? (lead.ownerAdminId ? `admin ${lead.ownerAdminId}` : 'Nobody yet')} />
          <Row label="Lead id" value={<DrillValue to={{ set: 'leads', filters: { id: lead.id } }} label={`Form from ${lead.email}`}>{lead.id}</DrillValue>} />
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1">Notes</div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="What happened when you followed up."
            data-testid="lead-notes"
            className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-accent/50"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => void saveNotes()}
              disabled={saving || !noteDirty}
              data-testid="lead-save-notes"
              className="btn-primary disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save note'}
            </button>
            {lead.ownerAdminEmail && (
              <span className="text-[11px] text-slate-500">
                last worked by {lead.ownerAdminEmail}
              </span>
            )}
          </div>

          {history.length > 1 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mt-5 mb-1">
                Everything from this address ({history.length})
              </div>
              <div className="space-y-1">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center gap-2 text-[11px] py-1 border-b border-white/5 last:border-0"
                  >
                    <span className="w-24 shrink-0 text-slate-300">
                      {LEAD_KIND_LABEL[h.kind] ?? h.kind}
                    </span>
                    <span className="text-slate-500">{dateTime(h.receivedAt)}</span>
                    {h.id === lead.id && <span className="text-accent">this one</span>}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-white/5">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-300">
                  Delete this lead? The email in the mailbox is the only other record.
                </span>
                <button
                  onClick={() => void remove()}
                  disabled={saving}
                  data-testid="lead-delete-confirm"
                  className="px-2.5 py-1 rounded-md text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10"
                >
                  Delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  Keep
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="lead-delete"
                className="text-xs text-slate-500 hover:text-red-300"
              >
                Delete lead
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Sorting and per-column filtering over whatever the server returned. The
// status tabs and the search box above still narrow server-side, because they
// also drive the counts; this narrows what came back.
const LEAD_COLUMNS: Column<RemoteLead>[] = [
  { key: 'received', label: 'Received', value: (l) => Date.parse(l.receivedAt) || 0, numeric: true },
  { key: 'kind', label: 'Form', value: (l) => LEAD_KIND_LABEL[l.kind] ?? l.kind },
  { key: 'who', label: 'Who', value: (l) => `${l.name} ${l.email}`.trim(), filter: 'text' },
  { key: 'company', label: 'Company', value: (l) => l.company ?? '', filter: 'text' },
  { key: 'source', label: 'Source', value: (l) => l.utmSource ?? l.utmCampaign ?? '' },
  { key: 'status', label: 'Status', value: (l) => STATUS_LABEL[l.status] ?? l.status }
]

// Leads from before the leads table existed live only as their notification
// emails. Pasting those here stores each with the date it was really sent and
// a note saying it was imported (server: leads.importHistorical).
function ImportPanel({ onImported }: { onImported: () => void }): JSX.Element {
  const preview = useOpenRow()
  const [text, setText] = useState('')
  const [source, setSource] = useState('the team inbox')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const parsed = useMemo(() => parseLeadEmails(text), [text])
  const ready = parsed.filter((p) => p.problems.length === 0)

  async function run(): Promise<void> {
    if (ready.length === 0) return
    setBusy(true)
    setError(null)
    const res = await importLeads(ready.map((p) => p.item as Parameters<typeof importLeads>[0][number]), source)
    setBusy(false)
    if (!res.ok) { setError(res.error); return }
    const { imported, duplicates, errors } = res.data
    setResult(`Imported ${imported}. ${duplicates ? `${duplicates} already stored, skipped. ` : ''}${errors.length ? `${errors.length} refused: ${errors.map((e) => e.error).join('; ')}.` : ''}`.trim())
    if (imported) { setText(''); onImported() }
  }

  return (
    <div className="glass-card rounded-xl p-4 mb-4" data-testid="leads-import">
      <div className="text-sm text-slate-300 mb-2">
        Paste the website&rsquo;s lead notification emails (as plain text, one or many). Each is stored with the date in its
        Received line and marked as imported. Nothing without a date is stored.
      </div>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setResult(null) }}
        rows={8}
        placeholder={'Demo request from the Plexii website.\n\nName: …\nEmail: …\nReceived: 2026-10-15T23:14:00.000Z'}
        data-testid="leads-import-text"
        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-accent"
      />
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <label className="text-xs text-slate-400">
          Where these came from{' '}
          <input value={source} onChange={(e) => setSource(e.target.value)} data-testid="leads-import-source" className="ml-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-xs" />
        </label>
        <button className="btn-primary" disabled={busy || ready.length === 0} onClick={() => void run()} data-testid="leads-import-run">
          {busy ? 'Importing…' : `Import ${ready.length} lead${ready.length === 1 ? '' : 's'}`}
        </button>
        {result && <span className="text-xs text-emerald-300" data-testid="leads-import-result">{result}</span>}
        {error && <span className="text-xs text-red-300">Import failed: {error}</span>}
      </div>
      {parsed.length > 0 && (
        <table className="admin-table mt-3" data-testid="leads-import-preview">
          <thead><tr><th>Kind</th><th>Name</th><th>Email</th><th>Sent</th><th>Problem</th></tr></thead>
          <tbody>
            {parsed.map((p, i) => (
              <ExpandableRow
                key={i}
                testId={`leads-import-row-${i}`}
                open={preview.isOpen(String(i))}
                onToggle={() => preview.toggle(String(i))}
                colSpan={5}
                detail={() => (
                  <DetailGrid
                    facts={[
                      ['Form', p.item.kind ? LEAD_KIND_LABEL[p.item.kind] ?? p.item.kind : 'Not recognised'],
                      ['Name', p.item.name],
                      ['Email', p.item.email],
                      ['Company', p.item.company],
                      ['Role', p.item.role],
                      ['Team size', p.item.teamSize],
                      ['Page', p.item.page],
                      ['Sent', p.item.at ? dateTime(p.item.at) : null],
                      ['Will it import', p.problems.length ? `No: ${p.problems.join('; ')}` : 'Yes']
                    ]}
                    wide={[['Message', p.item.message]]}
                  />
                )}
              >
                <td>{p.item.kind}</td>
                <td>{p.item.name || '—'}</td>
                <td>{p.item.email || '—'}</td>
                <td className="tabular-nums">{p.item.at || '—'}</td>
                <td className={p.problems.length ? 'text-amber-200' : 'text-slate-500'}>{p.problems.join('; ') || 'ready'}</td>
              </ExpandableRow>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default function Leads(): JSX.Element {
  const [importing, setImporting] = useState(false)
  const [leads, setLeads] = useState<RemoteLead[] | null>(null)
  const [counts, setCounts] = useState<LeadCounts | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<LeadStatus | 'all'>('all')
  const [kind, setKind] = useState<LeadKind | 'all'>('all')
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  // The open row is the selected lead; its other submissions load when it opens.
  const { openId: selected, toggle } = useOpenRow()
  const [history, setHistory] = useState<RemoteLead[]>([])
  const now = Date.now()

  // The search goes to the server (it matches company and campaign too, which
  // the loaded page may not hold), so it is debounced rather than sent per key.
  const debounce = useRef<number | undefined>(undefined)
  useEffect(() => {
    window.clearTimeout(debounce.current)
    debounce.current = window.setTimeout(() => setSearch(query.trim()), 250)
    return () => window.clearTimeout(debounce.current)
  }, [query])

  const load = useCallback(async (): Promise<void> => {
    const res = await listLeads({ status, kind, q: search || undefined, limit: 500 })
    if (res.ok) {
      setLeads(res.data.leads)
      setCounts(res.data.counts)
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }, [status, kind, search])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!selected) {
      setHistory([])
      return
    }
    let cancelled = false
    void getLead(selected).then((r) => {
      if (cancelled) return
      if (r.ok) setHistory(r.data.history)
    })
    return () => {
      cancelled = true
    }
  }, [selected])

  const t = useTableControls(leads ?? [], LEAD_COLUMNS, 'leads')

  const current = useMemo(
    () => (leads ?? []).find((l) => l.id === selected) ?? null,
    [leads, selected]
  )

  function onPatched(updated: RemoteLead): void {
    setLeads((prev) => (prev ?? []).map((l) => (l.id === updated.id ? updated : l)))
    // The status tabs carry counts, so they have to come from the server again
    // rather than be adjusted locally and drift.
    void load()
  }

  function onDeleted(id: string): void {
    setLeads((prev) => (prev ?? []).filter((l) => l.id !== id))
    if (selected === id) toggle(id)
    void load()
  }

  function exportCsv(): void {
    const rows = leads ?? []
    if (rows.length === 0) return
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `plexii-leads-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const header = (
    <div className="mb-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
          <h1 className="text-2xl font-semibold mt-1">Leads</h1>
          <div className="text-sm text-slate-400 mt-1">
            Every form sent on the website — live sessions, demos, early access and download-link
            requests — with where it came from.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImporting((v) => !v)} data-testid="leads-import-toggle" className="btn-ghost">
            {importing ? 'Close import' : 'Import past leads'}
          </button>
          <button
            onClick={exportCsv}
            disabled={!leads || leads.length === 0}
            data-testid="leads-export"
            className="btn-ghost disabled:opacity-40"
          >
            Export CSV
          </button>
        </div>
      </div>
      {importing && <div className="mt-4"><ImportPanel onImported={() => void load()} /></div>}

      <div className="flex flex-wrap items-center gap-1.5 mt-4">
        {(['all', ...LEAD_STATUSES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`leads-filter-${s}`}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              status === s
                ? 'border-accent/60 bg-accent/15 text-white'
                : 'border-white/10 text-slate-300 hover:border-white/20'
            }`}
          >
            {s === 'all' ? 'All' : STATUS_LABEL[s]}
            {counts && (
              <span className="ml-1.5 text-[11px] text-slate-400">
                {s === 'all' ? counts.total : counts.byStatus[s]}
              </span>
            )}
          </button>
        ))}
        <span className="w-px h-6 bg-white/10 mx-1" />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as LeadKind | 'all')}
          data-testid="leads-kind"
          className="rounded-md bg-white/[0.03] border border-white/10 px-2.5 py-1.5 text-sm text-slate-200"
        >
          <option value="all">Every form</option>
          {LEAD_KINDS.map((k) => (
            <option key={k} value={k}>
              {LEAD_KIND_LABEL[k]}
              {counts ? ` (${counts.byKind[k]})` : ''}
            </option>
          ))}
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, address, company or campaign"
          data-testid="leads-search"
          className="flex-1 min-w-[16rem] rounded-md bg-white/[0.03] border border-white/10 px-3 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-accent/50"
        />
      </div>
    </div>
  )

  if (state !== 'ready') {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {state === 'loading' ? 'Loading leads…' : `Could not load leads: ${error}`}
        </div>
      </div>
    )
  }

  const rows = leads ?? []
  const filtering = status !== 'all' || kind !== 'all' || search !== ''

  return (
    <div>
      {header}

      {rows.length > 0 && t.rows.length === 0 ? (
        <NoMatches t={t} noun="leads" />
      ) : rows.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="leads-empty">
          {filtering ? (
            <>No lead matches these filters.</>
          ) : (
            <>
              <div>No leads stored yet.</div>
              <div className="text-slate-500 mt-1">
                The website emails the team and posts each lead here. A lead sent before that was
                connected exists only as an email in the mailbox — this list does not reach back
                for it.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table">
            <TableHead t={t} />
            <tbody>
              {t.rows.map((l) => (
                <ExpandableRow
                  key={l.id}
                  testId="lead-row"
                  open={selected === l.id}
                  onToggle={() => toggle(l.id)}
                  colSpan={LEAD_COLUMNS.length}
                  detail={() =>
                    current && (
                      <Detail lead={current} history={history} onPatched={onPatched} onDeleted={onDeleted} onClose={() => toggle(l.id)} />
                    )
                  }
                >
                  <td className="text-xs text-slate-400 whitespace-nowrap">
                    <div>{dateTime(l.receivedAt)}</div>
                    <div className="text-slate-600">{ago(l.receivedAt, now)}</div>
                  </td>
                  <td className="text-slate-300 text-xs whitespace-nowrap">
                    {LEAD_KIND_LABEL[l.kind] ?? l.kind}
                  </td>
                  <td>
                    {l.name && <div className="text-slate-300 text-xs mb-0.5">{l.name}</div>}
                    <AddressCell lead={l} />
                  </td>
                  <td className="text-slate-300 text-xs">
                    {l.company || <span className="text-slate-600">—</span>}
                    {l.teamSize && <div className="text-slate-500">{l.teamSize}</div>}
                    {l.role && <div className="text-slate-500">{l.role}</div>}
                  </td>
                  <td className="max-w-[14rem]">
                    <SourceCell lead={l} />
                  </td>
                  <td>
                    <StatusPill status={l.status} />
                  </td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {counts && rows.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-500">
          Showing {t.rows.length} of {counts.total} stored {counts.total === 1 ? 'lead' : 'leads'}.
        </div>
      )}
    </div>
  )
}
